import { useState, useEffect } from 'react';
import { supabase } from '../shared/supabase';
import './Admin.css';

const JourneyForm = ({ editingJourney, onSuccess, onCancel }) => {

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    duration: '',
    gradient: '',
    is_premium: false,
    active: true,
    order_index: 0,
    image_url: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editingJourney) {
      setFormData(editingJourney);
      if (editingJourney.image_url) {
        setImagePreview(editingJourney.image_url);
      }
    }
  }, [editingJourney]);

  // Load form data from localStorage on mount
  useEffect(() => {
    if (!editingJourney) {
      const savedDraft = localStorage.getItem('journeyFormDraft');
      if (savedDraft) {
        try {
          const parsedData = JSON.parse(savedDraft);
          setFormData(parsedData);
        } catch (error) {
          console.error('Error loading draft:', error);
        }
      }
    }
  }, [editingJourney]);

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    if (!editingJourney) {
      localStorage.setItem('journeyFormDraft', JSON.stringify(formData));
    }
  }, [formData, editingJourney]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should be less than 5MB');
        return;
      }

      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async () => {
    if (!imageFile) return formData.image_url;

    try {
      setUploading(true);
      const fileExt = imageFile.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('journeys')
        .upload(filePath, imageFile);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('journeys')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.description) {
      alert('Please fill in all required fields');
      return;
    }

    setLoading(true);

    try {
      let imageUrl = formData.image_url;
      
      if (imageFile) {
        imageUrl = await uploadImage();
        if (!imageUrl) {
          setLoading(false);
          return;
        }
      }

      const journeyData = {
        ...formData,
        image_url: imageUrl,
        order_index: parseInt(formData.order_index) || 0
      };

      if (editingJourney) {
        const { error } = await supabase
          .from('journeys')
          .update(journeyData)
          .eq('id', editingJourney.id);

        if (error) throw error;
        alert('Journey updated successfully!');
      } else {
        const { error } = await supabase
          .from('journeys')
          .insert([journeyData]);

        if (error) throw error;
        alert('Journey created successfully!');
      }

      resetForm();
      onSuccess();
    } catch (error) {
      console.error('Error saving journey:', error);
      alert('Failed to save journey: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: '',
      duration: '',
      gradient: '',
      is_premium: false,
      active: true,
      order_index: 0,
      image_url: ''
    });
    setImageFile(null);
    setImagePreview('');
    localStorage.removeItem('journeyFormDraft');
  };

  return (
    <div className="journey-form-container">
      <div className="form-header">
        <h2>{editingJourney ? 'Edit Journey' : 'Create New Journey'}</h2>
        {editingJourney && (
          <button onClick={onCancel} className="btn-cancel">
            Cancel Edit
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="journey-form">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="title">Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Enter journey title"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="category">Category</label>
            <input
              type="text"
              id="category"
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              placeholder="e.g., Wealth, Health, Love"
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="description">Description *</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Enter journey description"
            rows="4"
            required
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="duration">Duration</label>
            <input
              type="text"
              id="duration"
              name="duration"
              value={formData.duration}
              onChange={handleInputChange}
              placeholder="e.g., 21 days, 3 months"
            />
          </div>

          <div className="form-group">
            <label htmlFor="gradient">Gradient Color</label>
            <input
              type="text"
              id="gradient"
              name="gradient"
              value={formData.gradient}
              onChange={handleInputChange}
              placeholder="e.g., linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="order_index">Order Index</label>
          <input
            type="number"
            id="order_index"
            name="order_index"
            value={formData.order_index}
            onChange={handleInputChange}
            placeholder="0"
          />
        </div>

        <div className="form-group">
          <label htmlFor="image">Journey Image</label>
          <input
            type="file"
            id="image"
            accept="image/*"
            onChange={handleImageChange}
            className="file-input"
          />
          {imagePreview && (
            <div className="image-preview">
              <img src={imagePreview} alt="Preview" />
            </div>
          )}
        </div>

        <div className="form-row checkbox-row">
          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="is_premium"
                checked={formData.is_premium}
                onChange={handleInputChange}
              />
              <span>Premium Journey</span>
            </label>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="active"
                checked={formData.active}
                onChange={handleInputChange}
              />
              <span>Active</span>
            </label>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className="btn-submit"
            disabled={loading || uploading}
          >
            {loading ? 'Saving...' : uploading ? 'Uploading...' : editingJourney ? 'Update Journey' : 'Create Journey'}
          </button>
          {editingJourney && (
            <button
              type="button"
              onClick={() => {
                resetForm();
                onCancel();
              }}
              className="btn-secondary"
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default JourneyForm;